import React, { useState, useMemo } from 'react';
import { 
  File, 
  FileText, 
  UserCheck, 
  Clock, 
  Search, 
  Filter, 
  Eye,
  Download,
  AlertTriangle,
  CheckCircle2
} from 'lucide-react';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';

// Enhanced Type Definitions
interface DocumentEntry {
  id: string;
  name: string;
  type: 'buyer' | 'seller' | 'service_provider';
  category: string;
  uploadedBy: string;
  uploadDate: string;
  expiryDate: string;
  status: 'active' | 'expired' | 'near_expiry' | 'pending_verification';
  verificationStatus: 'unverified' | 'in_progress' | 'verified' | 'rejected';
  userDetails: {
    id: string;
    name: string;
    email: string;
  };
}

const DocumentManagement: React.FC = () => {
  // Breadcrumb Management
  useBreadcrumbs([
        { label: 'Escrow Management', href: '#' },
        { label: 'Escrow Document Management' }
      ]);
  // Initial Document Entries
  const [documents, setDocuments] = useState<DocumentEntry[]>([
    {
      id: 'DOC001',
      name: 'Business License',
      type: 'seller',
      category: 'Business Registration',
      uploadedBy: 'john.seller@example.com',
      uploadDate: '2023-06-15',
      expiryDate: '2024-06-15',
      status: 'near_expiry',
      verificationStatus: 'in_progress',
      userDetails: {
        id: 'USER001',
        name: 'John Doe',
        email: 'john.seller@example.com'
      }
    },
    {
      id: 'DOC002',
      name: 'Passport',
      type: 'buyer',
      category: 'Identification',
      uploadedBy: 'jane.buyer@example.com',
      uploadDate: '2023-01-20',
      expiryDate: '2028-01-20',
      status: 'active',
      verificationStatus: 'verified',
      userDetails: {
        id: 'USER002',
        name: 'Jane Smith',
        email: 'jane.buyer@example.com'
      }
    },
    {
      id: 'DOC003',
      name: 'Tax Certificate',
      type: 'service_provider',
      category: 'Financial',
      uploadedBy: 'mike.provider@example.com',
      uploadDate: '2023-11-01',
      expiryDate: '2024-04-30',
      status: 'expired',
      verificationStatus: 'rejected',
      userDetails: {
        id: 'USER003',
        name: 'Mike Johnson',
        email: 'mike.provider@example.com'
      }
    }
  ]);

  // State for Filtering and Searching
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [verificationFilter, setVerificationFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Selected Document for Modal
  const [selectedDocument, setSelectedDocument] = useState<DocumentEntry | null>(null);
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);

  // Filtered Documents
  const filteredDocuments = useMemo(() => {
    return documents.filter(doc => {
      const matchesType = typeFilter === 'all' || doc.type === typeFilter;
      const matchesStatus = statusFilter === 'all' || doc.status === statusFilter;
      const matchesVerification = verificationFilter === 'all' || doc.verificationStatus === verificationFilter;
      const matchesSearch = 
        doc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        doc.userDetails.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        doc.category.toLowerCase().includes(searchQuery.toLowerCase());
      
      return matchesType && matchesStatus && matchesVerification && matchesSearch;
    });
  }, [documents, typeFilter, statusFilter, verificationFilter, searchQuery]);

  // Status Color and Icon Helpers
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'near_expiry': return 'bg-yellow-100 text-yellow-800';
      case 'expired': return 'bg-red-100 text-red-800';
      case 'pending_verification': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getVerificationColor = (status: string) => {
    switch (status) {
      case 'verified': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Document Details Modal
  const DocumentDetailsModal = () => {
    if (!selectedDocument) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white rounded-lg shadow-xl p-6 w-96 max-h-[90vh] overflow-y-auto">
          <h2 className="text-xl font-bold mb-4">Document Details</h2>
          <div className="space-y-4">
            <div>
              <span className="text-gray-500 block">Document Name</span>
              <p className="font-semibold">{selectedDocument.name}</p>
            </div>
            <div>
              <span className="text-gray-500 block">User</span>
              <p>{selectedDocument.userDetails.name}</p>
              <p className="text-sm text-gray-500">{selectedDocument.userDetails.email}</p>
            </div>
            <div>
              <span className="text-gray-500 block">Document Type</span>
              <p className="capitalize">{selectedDocument.type.replace('_', ' ')}</p>
            </div>
            <div>
              <span className="text-gray-500 block">Category</span>
              <p>{selectedDocument.category}</p>
            </div>
            <div>
              <span className="text-gray-500 block">Upload Date</span>
              <p>{selectedDocument.uploadDate}</p>
            </div>
            <div>
              <span className="text-gray-500 block">Expiry Date</span>
              <p>{selectedDocument.expiryDate}</p>
            </div>
            <div>
              <span className="text-gray-500 block">Status</span>
              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(selectedDocument.status)}`}>
                {selectedDocument.status.replace('_', ' ').charAt(0).toUpperCase() + selectedDocument.status.replace('_', ' ').slice(1)}
              </span>
            </div>
            <div>
              <span className="text-gray-500 block">Verification Status</span>
              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getVerificationColor(selectedDocument.verificationStatus)}`}>
                {selectedDocument.verificationStatus.replace('_', ' ').charAt(0).toUpperCase() + selectedDocument.verificationStatus.replace('_', ' ').slice(1)}
              </span>
            </div>
          </div>
          <div className="mt-6 flex justify-end space-x-2">
            <button 
              onClick={() => setIsDocumentModalOpen(false)}
              className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
            >
              Close
            </button>
            <button 
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              <Download className="inline mr-2 h-4 w-4" /> Download
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">
            Document Management
          </h1>

          {/* Filters and Search */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex flex-wrap items-center space-x-4 space-y-2">
              <div className="relative flex-grow">
                <input
                  type="text"
                  placeholder="Search documents, users, categories..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              </div>
              
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Types</option>
                <option value="buyer">Buyers</option>
                <option value="seller">Sellers</option>
                <option value="service_provider">Service Providers</option>
              </select>
              
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="near_expiry">Near Expiry</option>
                <option value="expired">Expired</option>
              </select>
              
              <select
                value={verificationFilter}
                onChange={(e) => setVerificationFilter(e.target.value)}
                className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Verifications</option>
                <option value="verified">Verified</option>
                <option value="in_progress">In Progress</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>

          {/* Document List */}
          <div className="bg-white rounded-lg shadow">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Document Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Upload Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Expiry Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Verification
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredDocuments.map((doc) => (
                    <tr key={doc.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {doc.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {doc.userDetails.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                        {doc.type.replace('_', ' ')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {doc.uploadDate}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {doc.expiryDate}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(doc.status)}`}>
                          {doc.status.replace('_', ' ').charAt(0).toUpperCase() + doc.status.replace('_', ' ').slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getVerificationColor(doc.verificationStatus)}`}>
                          {doc.verificationStatus.replace('_', ' ').charAt(0).toUpperCase() + doc.verificationStatus.replace('_', ' ').slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button 
                          onClick={() => {
                            setSelectedDocument(doc);
                            setIsDocumentModalOpen(true);
                          }}
                          className="text-blue-600 hover:text-blue-900 flex items-center justify-end"
                        >
                          <Eye className="mr-1 h-4 w-4" /> View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Document Details Modal */}
          {isDocumentModalOpen && selectedDocument && (
            <DocumentDetailsModal />
          )}

          {/* Document Summary Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Documents</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {documents.length}
                  </p>
                </div>
                <FileText className="h-8 w-8 text-blue-500" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Documents Near Expiry</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {documents.filter(doc => doc.status === 'near_expiry').length}
                  </p>
                </div>
                <AlertTriangle className="h-8 w-8 text-yellow-500" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Verified Documents</p>
                  <p className="text-2xl font-bold text-green-600">
                    {documents.filter(doc => doc.verificationStatus === 'verified').length}
                  </p>
                </div>
                <CheckCircle2 className="h-8 w-8 text-green-500" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentManagement;